/* Internationalisation for measurement units.
 *
 * Field names carry a unit suffix that encodes the display policy:
 *  - `_x`         jurisdictional: the value is already in the client's units
 *                 (e.g. a legislated speed limit); never converted, only labelled.
 *  - `_m` / `_ft` stored in a fixed unit; converted to the client's system for display.
 *
 * The physical quantity and display precision come from each app's field
 * registry (see `columns.ts`), never from the suffix.
 */

export type UnitSystem = 'metric' | 'imperial';
export type Quantity = 'length' | 'bigLength' | 'speed';

export interface UnitField {
	quantity: Quantity;
	/** Display decimal places; defaults to 0. */
	precision?: number;
}

// Intl unit token + factor relative to the metric canonical (metre, km/h).
const UNITS: Record<Quantity, Record<UnitSystem, { intlUnit: string; perMetric: number }>> = {
	length: {
		metric: { intlUnit: 'meter', perMetric: 1 },
		imperial: { intlUnit: 'foot', perMetric: 3.280839895 }
	},
	bigLength: {
		metric: { intlUnit: 'kilometer', perMetric: 1 },
		imperial: { intlUnit: 'mile', perMetric: 0.6213712 }
	},
	speed: {
		metric: { intlUnit: 'kilometer-per-hour', perMetric: 1 },
		imperial: { intlUnit: 'mile-per-hour', perMetric: 0.621371192 }
	}
};

// Unit strings surfaced by the metadata table (`distance_units` / `speed_units`, e.g. 'feet', 'mph')
// mapped to a UnitSystem; extra aliases tolerate spelling variants from the source data.
const DB_UNIT_ALIASES: Record<Quantity, Record<UnitSystem, string[]>> = {
	length: {
		metric: ['meters', 'metres', 'meter', 'metre', 'm'],
		imperial: ['feet', 'foot', 'ft']
	},
	bigLength: {
		metric: ['kilometers', 'kilometres', 'kilometer', 'kilometre', 'km'],
		imperial: ['miles', 'mile', 'mi', 'mi.']
	},
	speed: {
		metric: ['km/h', 'kph', 'kmh'],
		imperial: ['mph', 'mi/h']
	}
};

/** Resolves a client-config unit string to a UnitSystem, or null if unrecognised. */
export const systemFromDbUnit = (quantity: Quantity, dbUnit: string): UnitSystem | null => {
	const needle = dbUnit.trim().toLowerCase();
	const aliases = DB_UNIT_ALIASES[quantity];
	return (
		(Object.keys(aliases) as UnitSystem[]).find((sys) => aliases[sys].includes(needle)) ?? null
	);
};

const STORAGE_SUFFIXES: Record<string, UnitSystem> = {
	m: 'metric',
	ft: 'imperial'
};

// Determine if field is metric or imperial, or null for a jurisdictional (`_x`) field.
const parseStorage = (fieldName: string): UnitSystem | null =>
	STORAGE_SUFFIXES[fieldName.slice(fieldName.lastIndexOf('_') + 1)] ?? null;

export const convert = (
	value: number,
	quantity: Quantity,
	from: UnitSystem,
	to: UnitSystem
): number => {
	if (from === to) return value;
	return (value / UNITS[quantity][from].perMetric) * UNITS[quantity][to].perMetric;
};

export const getUnitLabel = (
	quantity: Quantity,
	system: UnitSystem,
	display: 'short' | 'long' = 'short'
): string =>
	new Intl.NumberFormat('en-US', {
		style: 'unit',
		unit: UNITS[quantity][system].intlUnit,
		unitDisplay: display
	})
		.formatToParts(0)
		.find((part) => part.type === 'unit')?.value ?? '';

/** Converts (unless jurisdictional) and rounds a value for display; returns the number only, no unit. */
export const formatUnitValue = (
	fieldName: string,
	value: number,
	field: UnitField,
	system: UnitSystem
): string => {
	if (!field) return String(value);
	if (value === undefined) return '';
	const stored = parseStorage(fieldName);
	const display = stored ? convert(value, field.quantity, stored, system) : value;
	return new Intl.NumberFormat('en-US', { maximumFractionDigits: field.precision ?? 0 }).format(
		display
	);
};
